import React from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { IStore } from "../../../../redux/store";
import {
	IBulkDetails,
	IOption,
	IVDMDashboardContent,
	ITankerGasDetails,
	IContainerDetails,
} from "redux/app/vdmdashboard/@types";
import { Tooltip } from "react-tippy";

interface IOptionWithTooltip extends IOption {
	tooltip?: string;
}

type OwnProps = {
	data: IVDMDashboardContent["cargo"];
};

type Card2Props = PropsFromRedux & OwnProps;

function Card2(props: Card2Props) {
	const { data } = props;
	if (!data || !data.details) return null;

	const { type, details } = data;

	const getMetrics = (): IOptionWithTooltip[] => {
		// Common metrics available for ALL types
		const commonMetrics: IOptionWithTooltip[] = [
			{ label: "Elapsed", value: `${(details as any).elapsedHours} Hrs` },
		];

		switch (type) {
			case "container": {
				const d = details as IContainerDetails;
				return [
					...commonMetrics,
					{ label: "STS", value: (details as any)?.sts },
					{
						label: "Loaded Units",
						value: `${d.summary.totalLoadedContainerUnits} `,
					},
					{
						label: "Disch. Units",
						value: `${d.summary.totalDischargedContainerUnits} `,
					},
					{
						label: "Loaded wt.",
						value: `${d.summary.totalLoadedContainerWeight.toFixed(1)} MT`,
					},
					{
						label: "Discharged wt.",
						value: `${d.summary.totalDischargedContainerWeight.toFixed(1)} MT`,
					},
				];
			}

			case "tanker":
			case "gas": {
				const d = details as ITankerGasDetails;

				// Logic for Cargo Types truncation similar to bulk
				const cargoTypes = d.cargoes.map((c) => c.cargoType);
				const firstType = cargoTypes[0] || "N/A";
				const remainingCount = cargoTypes.length - 1;

				const displayValue =
					remainingCount > 0
						? `${firstType} +${remainingCount} more`
						: `${firstType}`;

				// Create a formatted string for the tooltip
				const tooltipContent = d.cargoes
					.map((c) => {
						const temp = c?.temperature ? `${c?.temperature} °C` : "N/A";
						return `${c?.cargoType} | Density: ${c?.density || "N/A"} | Temp: ${temp}`;
					})
					.join("\n");

				return [
					...commonMetrics,
					{
						label: "Loaded",
						value: `${d?.summary?.totalCargoLoaded.toFixed(1)} MT`,
					},
					{
						label: "Discharged",
						value: `${d?.summary?.totalCargoDischarged.toFixed(1)} MT`,
					},
					{
						label: "Cargo Type",
						value: displayValue,
						tooltip: tooltipContent,
					},
				];
			}

			case "pctc": {
				const d = details as any;
				return [
					...commonMetrics,
					{ label: "Veh. Loaded", value: `${d.summary.totalVehiclesLoaded} ` },
					{
						label: "Veh. Disch.",
						value: `${d.summary.totalVehiclesDischarged} `,
					},
					{
						label: "Weight Loaded",
						value: `${d.summary.totalLoadedVehicleWeight.toFixed(1)} MT`,
					},
					{
						label: "Weight Disch.",
						value: `${d.summary.totalDischargedVehicleWeight.toFixed(1)} MT`,
					},
				];
			}

			case "bulk": {
				const d = details as { type: "bulk" } & IBulkDetails;

				// Logic for Cargo Types truncation
				const cargoTypes = d.cargoes.map((c) => c.cargoType);
				const firstType = cargoTypes[0] || "N/A";
				const remainingCount = cargoTypes.length - 1;

				const displayValue =
					remainingCount > 0
						? `${firstType} +${remainingCount} more`
						: `${firstType}`;

				const tooltipContent = cargoTypes.join(", ");
				return [
					...commonMetrics,
					{ label: "STS", value: (details as any)?.sts },
					{
						label: "Loaded",
						value: `${d?.summary?.totalCargoLoaded.toFixed(1)} MT`,
					},
					{
						label: "Discharged",
						value: `${d?.summary?.totalCargoDischarged.toFixed(1)} MT`,
					},
					{
						label: "Cargo Type",
						value: displayValue,
						tooltip: tooltipContent,
					},
				];
			}

			default:
				return commonMetrics;
		}
	};

	const metricsToShow = getMetrics();

	return (
		<div className="flex-1 hover:cursor-pointer 2xl:w-full">
			<div className="bg-gradient-to-b from-white to-[#F9FCFE] border border-gray-200 rounded-xl p-4 px-2.5 shadow-sm xl:max-w-[320px] h-full sm:h-[179px] min-w-[176px] 2xl:w-full 2xl:max-w-full flex flex-col justify-between hover:shadow relative transition-all">
				<div className="h-full flex flex-col justify-between">
					<div>
						<h3 className="text-xs text-gray-800 font-medium mb-1 py-1 border-l-4 border-l-[#3182CE] px-2 bg-gradient-to-r from-[#3182CE29] to-[#3182CE00] w-1/2">
							Cargo
						</h3>
						<p className="text-sm lg:text-base xl:text-lg font-medium text-gray-800 mb-3 whitespace-pre-line">
							{type.toUpperCase()}
						</p>
					</div>

					<div className="space-y-2 text-xs">
						<div className="grid grid-cols-2 gap-y-2 w-full">
							{metricsToShow.map((metric) => {
								const { label, value, tooltip } = metric;

								if (tooltip) {
									return (
										<Tooltip
											key={label}
											html={
												<div className="p-0.5 whitespace-pre-line text-sm">
													{tooltip}
												</div>
											}
											position="bottom"
											trigger="mouseenter"
											arrow
											theme="light"
										>
											<div className="flex gap-2 items-center cursor-help">
												<span className="text-gray-500 font-medium inline-block w-[75px]">
													{label}
												</span>
												<span className="text-gray-700 font-medium truncate">
													{value}
												</span>
												<span className="bp3-icon bp3-icon-info-sign text-blue-400 -ml-1.5 mr-1" />
											</div>
										</Tooltip>
									);
								}

								return (
									<div key={label} className="flex gap-2 items-center">
										<span className="text-gray-500 font-medium inline-block w-[75px]">
											{label}
										</span>
										<span className="text-gray-700 font-medium truncate">
											{value}
										</span>
									</div>
								);
							})}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	return {};
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {};
}

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;

export default reduxConnector(Card2);
